'use client';

/**
 * SwipeTrack — finger-tracking carousel primitive for the mobile pinned-card
 * (design turn 14b). Generic over item type so both the compact course body
 * and the compact media body reuse the same gesture mechanics; the caller
 * supplies its own renderItem + header chrome (PinHeaderNav's arrows/dots)
 * and owns the current `index` (so the header can stay in sync without a
 * second source of truth).
 *
 * Desktop is UNCHANGED — the owner scoped this redesign to mobile only.
 * CardStack (primitives/card-stack.tsx) keeps rendering the desktop pinned
 * rail exactly as before; this is a new, separate primitive rather than a
 * CardStack rewrite (the brief's explicit instruction).
 *
 * Gesture contract:
 *  - `touch-action: pan-y` on the container — a vertical drag starting on
 *    the card must still scroll the transcript underneath. We only ever
 *    call `preventDefault`/capture the pointer once axis lock (below) has
 *    resolved horizontal; until then the browser is free to start its own
 *    vertical scroll.
 *  - Axis lock (resolveAxisLock) decides horizontal vs vertical once the
 *    drag clears a small slop. Below the slop, or once locked vertical, this
 *    is not our gesture: no transform, and the eventual pointerup is a tap
 *    (onTap) rather than a drag — this is also what stops a horizontal drag
 *    from ALSO firing tap-to-expand (only an `undecided`-axis release
 *    counts as a tap; a horizontal-locked release never does, committed or
 *    not).
 *  - Release (resolveSwipeCommit): past ~40px commits to the neighbor,
 *    otherwise springs back. No CSS transition while a finger is down (1:1
 *    tracking); the transition turns on only for the post-release settle.
 *  - prefers-reduced-motion (useReducedMotion, the house pattern — see
 *    contact-info-card.tsx/handoff-recap-card.tsx): the settle is an
 *    instant transform, not a fight over whether 1:1 finger tracking itself
 *    counts as "motion" — it doesn't; only the programmatic settle does.
 *
 * Position is driven by React state (`dragPx`, `index`) rather than an
 * imperative style mutation on a ref. That costs a re-render per
 * pointermove, which is fine here — stacks top out at 5 items and this
 * isn't a per-frame audio-rate path (contrast useAudioVisualization's level
 * ref, which specifically avoids React state for that reason) — and keeping
 * the position in state is what makes the commit/spring-back transition
 * (driven by the SAME `index`/`dragPx` values) fall out for free from a
 * single declarative style expression.
 */

import {
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type DragAxis = 'horizontal' | 'vertical' | 'undecided';

/** Minimum displacement (px) before either axis commits — below it the
 *  gesture is still 'undecided' (could still resolve to a tap). */
export const AXIS_SLOP_PX = 8;

/** Distance (px) a horizontal drag must clear at release to commit to the
 *  neighbor instead of springing back. */
export const SWIPE_COMMIT_THRESHOLD_PX = 40;

/** Pure axis-lock decision: is an in-progress drag a horizontal swipe (this
 *  track) or a vertical scroll (the transcript underneath)? */
export function resolveAxisLock(
  dx: number,
  dy: number,
  slopPx: number = AXIS_SLOP_PX
): DragAxis {
  if (Math.abs(dx) < slopPx && Math.abs(dy) < slopPx) return 'undecided';
  return Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
}

export interface SwipeCommitResult {
  index: number;
  committed: boolean;
}

/** Pure release-time decision: does the drag commit to a neighbor, or spring
 *  back? Distance-only (no velocity) per the design brief. Clamped at the
 *  ends — dragging past the first/last item always springs back, however
 *  far it traveled. */
export function resolveSwipeCommit(
  index: number,
  itemCount: number,
  dragPx: number,
  thresholdPx: number = SWIPE_COMMIT_THRESHOLD_PX
): SwipeCommitResult {
  if (Math.abs(dragPx) < thresholdPx) return { index, committed: false };
  const next = index + (dragPx < 0 ? 1 : -1);
  if (next < 0 || next > itemCount - 1) return { index, committed: false };
  return { index: next, committed: true };
}

interface Gesture {
  startX: number;
  startY: number;
  axis: DragAxis;
  pointerId: number;
}

export interface SwipeTrackProps<T> {
  items: T[];
  /** Current position — owned by the caller so header dots/arrows
   *  (PinHeaderNav) share one source of truth with the track. */
  index: number;
  onIndexChange: (index: number) => void;
  renderItem: (item: T, index: number) => ReactNode;
  /** Fired on a plain tap: pointerdown→pointerup with the axis never
   *  resolving (displacement stayed under the slop in both directions).
   *  Never fires after a horizontal drag, committed or sprung back — see
   *  the file header's disambiguation note. */
  onTap?: () => void;
  className?: string;
}

export function SwipeTrack<T>({
  items,
  index,
  onIndexChange,
  renderItem,
  onTap,
  className,
}: SwipeTrackProps<T>) {
  const reducedMotion = useReducedMotion();
  const gestureRef = useRef<Gesture | null>(null);
  const [dragPx, setDragPx] = useState(0);
  const [dragging, setDragging] = useState(false);

  if (items.length === 0) return null;
  // Single item: no chrome, no gesture wiring — matches CardStack's own
  // "1 item renders plainly" convention.
  if (items.length === 1) {
    return <div className={className}>{renderItem(items[0], 0)}</div>;
  }

  const clampedIndex = Math.min(Math.max(index, 0), items.length - 1);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    gestureRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      axis: 'undecided',
      pointerId: e.pointerId,
    };
    setDragging(true);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = gestureRef.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;

    if (g.axis === 'undecided') {
      g.axis = resolveAxisLock(dx, dy);
      if (g.axis === 'horizontal') {
        // Claim the gesture now — capture so a finger sliding past the
        // track's own edge doesn't lose pointermove events. jsdom/older
        // browsers may not implement this; it's a pure enhancement.
        try {
          e.currentTarget.setPointerCapture(g.pointerId);
        } catch {
          // no-op — capture is best-effort
        }
      }
    }
    if (g.axis !== 'horizontal') return; // vertical/undecided: let the page scroll

    e.preventDefault();
    setDragPx(dx);
  };

  const endGesture = () => {
    const g = gestureRef.current;
    gestureRef.current = null;
    setDragging(false);

    if (!g || g.axis !== 'horizontal') {
      setDragPx(0);
      // Only an undecided release is a tap — a vertical-locked release was
      // a scroll gesture, not a tap on the card.
      if (!g || g.axis === 'undecided') onTap?.();
      return;
    }

    const result = resolveSwipeCommit(clampedIndex, items.length, dragPx);
    setDragPx(0);
    if (result.committed) onIndexChange(result.index);
  };

  return (
    <div
      className={cn('overflow-hidden', className)}
      style={{ touchAction: 'pan-y' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
    >
      <div
        className="flex"
        style={{
          transform: `translateX(calc(${-clampedIndex * 100}% + ${dragPx}px))`,
          transition:
            dragging || reducedMotion ? 'none' : 'transform .3s ease-out',
        }}
      >
        {items.map((item, i) => (
          <div key={i} style={{ flex: '0 0 100%', minWidth: 0 }}>
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    </div>
  );
}
